import { useEffect, useRef, useState } from "react";
import styles from "../styles/selectLocationModal.module.css";
import { useNavigate } from "react-router-dom";
import Table from "./table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getOrganizationList,
  getLocationList,
  switchAccountLocation,
} from "../services/admin/organization";
import Spinner from "./spinner";
import toast from "react-hot-toast";
const SelectLocationModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const modalRef = useRef(null);
  const currentAccountId = sessionStorage.getItem("acc_id");
  const [currentLocation, setCurrentLocation] = useState(
    JSON.parse(sessionStorage.getItem("current_loc")) || []
  );

  const {
    data: organizations,
    isLoading: isOrganizationsLoading,
    refetch: refetchOrg,
  } = useQuery(
    ["organizations-list"],
    () => getOrganizationList(),
    {
      enabled: true,
    }
  );

  const sortedOrganizations = organizations
    ?.map((org) => ({
      id: org?.id,
      name: org?.id === currentAccountId ? `${org?.name} (Active)` : org?.name,
      locations_count: org?.locations_count,
      isCurrent: org?.id === currentAccountId,
      schema_name: org?.schema_name,
    }))
    .sort((a, b) => (a.isCurrent ? -1 : b.isCurrent ? 1 : 0))
    .map(({ isCurrent, ...rest }) => rest);

  const switchLocationMutation = useMutation(
    (data) => switchAccountLocation(data),
  );

  const handleChangeOrganization = async (org) => {
    const currentAccId = sessionStorage.getItem("acc_id");
    if (String(currentAccId) === String(org?.id)) {
      toast.error(`Account already active`);
      return;
    }

    setCurrentLocation(org);

    // Save previous state for rollback on API failure
    const prevUser = localStorage.getItem("user");
    const prevAccId = sessionStorage.getItem("acc_id");
    const prevAccName = sessionStorage.getItem("acc_name");
    const prevSchema = sessionStorage.getItem("schema_name");
    const prevCurrentLoc = sessionStorage.getItem("current_loc");
    const prevFallbackLocation = localStorage.getItem("fallback_location");
    const prevFallbackOrg = localStorage.getItem("fallback_org");

    const rollback = () => {
      if (prevUser) localStorage.setItem("user", prevUser);
      if (prevAccId) sessionStorage.setItem("acc_id", prevAccId);
      if (prevAccName) sessionStorage.setItem("acc_name", prevAccName);
      if (prevSchema) sessionStorage.setItem("schema_name", prevSchema);
      if (prevCurrentLoc) sessionStorage.setItem("current_loc", prevCurrentLoc);
      if (prevFallbackLocation) localStorage.setItem("fallback_location", prevFallbackLocation);
      if (prevFallbackOrg) localStorage.setItem("fallback_org", prevFallbackOrg);
    };

    // Update storage before getLocationList (it reads acc_id from sessionStorage)
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      user.current_account = org?.id;
      user.current_location = null;
      user.current_schema = org?.schema_name;
      localStorage.setItem("user", JSON.stringify(user));
    }

    sessionStorage.setItem("acc_id", org?.id);
    sessionStorage.setItem("acc_name", org?.name || "");
    sessionStorage.setItem("schema_name", org?.schema_name);
    sessionStorage.removeItem("current_loc");
    localStorage.removeItem("fallback_location");
    localStorage.setItem("fallback_org", JSON.stringify(org));

    // Resolve a location_id for the target account before switching.
    // Backend requires it when target account == current account (treated as a "same-account" switch).
    let location_id;
    let locationsErrorStatus;
    try {
      await getLocationList(true);
      const cur = JSON.parse(sessionStorage.getItem("current_loc") || "null");
      location_id = cur?.id ? String(cur.id) : undefined;
    } catch (error) {
      locationsErrorStatus = error?.response?.status;
    }

    if (locationsErrorStatus === 403 || locationsErrorStatus === 401) {
      rollback();
      toast.error("You don't have access to this account.");
      return;
    }

    if (locationsErrorStatus) {
      rollback();
      toast.error("Couldn't load locations for this account. Please try again.");
      return;
    }

    if (!location_id) {
      rollback();
      toast.error("This account has no locations configured.");
      return;
    }

    const data = {
      account_id: org?.id,
      location_id,
    };

    try {
      await switchLocationMutation.mutateAsync(data);
    } catch (error) {
      rollback();
      const status = error?.response?.status;
      if (status === 403 || status === 401) {
        toast.error("You don't have access to this account.");
      } else {
        toast.error(
          error?.response?.data?.error || "Failed to switch account. Please try again."
        );
      }
      return;
    }

    queryClient.clear();
    onClose();
    // Hard reload — ensures every singleton/cache rehydrates with the new account context.
    window.location.href = "/dashboard";
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const locationTableColumns = [
    {
      key: "name",
      label: "Accounts",
      alignLeft: true,
    },
    {
      key: "locations_count",
      label: "Locations",
    },
  ];

  return (
    <div className={styles.overlay}>
      <div
        className={styles.modal}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="switch-account-modal-title"
      >
        <div className={styles.comp_header}>
          <p id="switch-account-modal-title" className={styles.comp_heading}>
            Select Account To Switch
          </p>
        </div>

        <div className={styles.comp_wrapper}>
          <p>Click the account you would like to switch</p>

          <div className={styles.table_wrapper}>
            <Table
              columns={locationTableColumns}
              data={sortedOrganizations ? sortedOrganizations : []}
              height={250}
              rowClass={(row) =>
                row.id === currentAccountId ? "selected_loc_row" : ""
              }
              isRowClickable={true}
              onRowClick={(org) => handleChangeOrganization(org)}
              loading={isOrganizationsLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectLocationModal;
